import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface InstructionsDirectory {
    directoryId: string;
    name: string;
    isRootDirectory: boolean;
    parentDirectoryId: string | null;
    createdAt: string;
    updatedAt: string;
    seqId?: number;
}

export interface InstructionsFile {
    fileId: string;
    seqId: number;
    directoryId: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    fileUrl: string;
    fileMetadata?: unknown;
    createdAt: string;
    updatedAt: string;
}

export interface InstructionsTree {
    /**
     * All directories in a flat list (used for breadcrumbs/path building).
     */
    allDirs: InstructionsDirectory[];
    /**
     * Root-level directories (parentDirectoryId === null).
     */
    roots: InstructionsDirectory[];
    /**
     * Children grouped by parent directory id. Root children are stored under the
     * special key "__root".
     */
    childrenByParent: Record<string, InstructionsDirectory[]>;
    /**
     * Files grouped by directory id.
     */
    filesByDirectoryId: Record<string, InstructionsFile[]>;
}

export interface DoctorInstructionsState {
    selectedDoctorUuid: string | null;
    loading: boolean;
    error: string | null;
    tree: InstructionsTree | null;
}

const initialState: DoctorInstructionsState = {
    selectedDoctorUuid: null,
    loading: false,
    error: null,
    tree: null,
};

const doctorInstructionsSlice = createSlice({
    name: 'doctorInstructions',
    initialState,
    reducers: {
        setSelectedDoctorUuid: (state, action: PayloadAction<string | null>) => {
            state.selectedDoctorUuid = action.payload;
        },
        setInstructionsLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },
        setInstructionsError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            state.loading = false;
        },
        setInstructionsTree: (state, action: PayloadAction<InstructionsTree | null>) => {
            state.tree = action.payload;
            state.loading = false;
            state.error = null;
        },
        clearDoctorInstructionsState: () => initialState,
    },
});

export const {
    setSelectedDoctorUuid,
    setInstructionsLoading,
    setInstructionsError,
    setInstructionsTree,
    clearDoctorInstructionsState,
} = doctorInstructionsSlice.actions;

export default doctorInstructionsSlice.reducer;

